import type { ReactNode } from "react";
import type React from "react";
import { cn } from "@/lib/utils";

/**
 * Simple loading wrapper — shows `fallback` shimmer when loading, children when done.
 */
export function Shimmer({
  loading,
  fallback,
  children,
}: {
  loading: boolean;
  fallback: ReactNode;
  children: ReactNode;
}) {
  return <>{loading ? fallback : children}</>;
}

function ShimmerBlock({ className, style }: { className?: string; style?: React.CSSProperties }) {
  return (
    <div
      className={cn(
        "rounded-md bg-muted relative overflow-hidden",
        className
      )}
      style={style}
    >
      <div className="absolute inset-0 -translate-x-full animate-[shimmer_1.5s_infinite] bg-gradient-to-r from-transparent via-white/40 to-transparent" />
    </div>
  );
}

export function StatCardShimmer() {
  return (
    <div className="rounded-xl border border-border bg-card p-6 h-full">
      <div className="flex items-center justify-between mb-4">
        <ShimmerBlock className="h-3.5 w-24" />
        <ShimmerBlock className="h-9 w-9 rounded-lg" />
      </div>
      <ShimmerBlock className="h-8 w-36 mb-2" />
      <ShimmerBlock className="h-3.5 w-20" />
    </div>
  );
}

export function ChartShimmer({ height = 320 }: { height?: number }) {
  return (
    <div className="rounded-xl border border-border bg-card p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <ShimmerBlock className="h-5 w-32 mb-2" />
          <ShimmerBlock className="h-3.5 w-48" />
        </div>
      </div>
      <ShimmerBlock className="w-full" style={{ height }} />
    </div>
  );
}

export function TableShimmer({ rows = 8, cols = 6 }: { rows?: number; cols?: number }) {
  return (
    <div className="rounded-xl border border-border bg-card p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <ShimmerBlock className="h-5 w-40 mb-2" />
          <ShimmerBlock className="h-3.5 w-28" />
        </div>
      </div>
      <ShimmerBlock className="h-9 w-56 mb-4" />
      <div className="space-y-3">
        {/* Header */}
        <div className="flex gap-4">
          {Array.from({ length: cols }).map((_, i) => (
            <ShimmerBlock key={i} className="h-4 flex-1" />
          ))}
        </div>
        {/* Rows */}
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="flex gap-4">
            {Array.from({ length: cols }).map((_, j) => (
              <ShimmerBlock key={j} className="h-4 flex-1" />
            ))}
          </div>
        ))}
      </div>
      <div className="flex justify-between items-center mt-4 pt-3">
        <ShimmerBlock className="h-4 w-28" />
        <ShimmerBlock className="h-4 w-20" />
      </div>
    </div>
  );
}

export function CardGridShimmer({ count = 8 }: { count?: number }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="rounded-xl border border-border bg-card p-5">
          <div className="flex items-start justify-between mb-4">
            <ShimmerBlock className="h-12 w-12 rounded-lg" />
            <ShimmerBlock className="h-5 w-16 rounded-full" />
          </div>
          <ShimmerBlock className="h-4 w-28 mb-2" />
          <ShimmerBlock className="h-3 w-full mb-1" />
          <ShimmerBlock className="h-3 w-3/4 mb-4" />
          <div className="border-t pt-3 flex justify-between">
            <ShimmerBlock className="h-3 w-16" />
            <ShimmerBlock className="h-4 w-12" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function AnalyticsCardsShimmer({ count = 3 }: { count?: number }) {
  return (
    <div className={`grid grid-cols-1 sm:grid-cols-${count} gap-6`}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="rounded-xl border border-border bg-card p-6 h-full">
          <div className="flex items-center gap-2 mb-4">
            <ShimmerBlock className="h-5 w-5 rounded" />
            <ShimmerBlock className="h-3.5 w-28" />
          </div>
          <ShimmerBlock className="h-9 w-32 mb-2" />
          <ShimmerBlock className="h-3 w-24" />
        </div>
      ))}
    </div>
  );
}

export function SettingsShimmer() {
  return (
    <div className="rounded-xl border border-border bg-card p-6">
      <div className="flex flex-col items-center mb-6">
        <ShimmerBlock className="h-[72px] w-[72px] rounded-full mb-3" />
        <ShimmerBlock className="h-6 w-40 mb-2" />
        <ShimmerBlock className="h-4 w-24" />
      </div>
      <ShimmerBlock className="h-px w-full mb-4" />
      <div className="space-y-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="flex items-center gap-3">
            <ShimmerBlock className="h-5 w-5 rounded" />
            <div className="flex-1">
              <ShimmerBlock className="h-3 w-12 mb-1.5" />
              <ShimmerBlock className="h-4 w-40" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function DeveloperSettingsShimmer() {
  return (
    <div className="space-y-4">
      <ShimmerBlock className="h-10 w-72 rounded-lg" />
      <div className="rounded-xl border border-border bg-card p-6">
        <ShimmerBlock className="h-5 w-24 mb-2" />
        <ShimmerBlock className="h-3.5 w-80 mb-5" />
        <ShimmerBlock className="h-px w-full mb-5" />
        <ShimmerBlock className="h-10 w-full mb-3" />
        <ShimmerBlock className="h-10 w-full mb-3" />
        <ShimmerBlock className="h-9 w-32" />
      </div>
    </div>
  );
}

// Inline the shimmer keyframe via a style tag (injected once)
if (typeof document !== "undefined" && !document.getElementById("shimmer-keyframes")) {
  const style = document.createElement("style");
  style.id = "shimmer-keyframes";
  style.textContent = `@keyframes shimmer { 100% { transform: translateX(100%); } }`;
  document.head.appendChild(style);
}
